import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../App";
import Watermark from '../components/Watermark';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion',
};

function formatKickoff(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleDateString('en-BW', { weekday: 'short', day: 'numeric', month: 'short' }) +
    ' ' + d.toLocaleTimeString('en-BW', { hour: '2-digit', minute: '2-digit' });
}

function timeAgo(dt) {
  if (!dt) return '—';
  const mins = Math.floor((Date.now() - new Date(dt)) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

function MagnitudePill({ magnitude }) {
  const config = {
    LARGE: { label: '🔥 Large', color: 'var(--danger)' },
    MEDIUM: { label: '⚡ Medium', color: 'var(--warning)' },
    SMALL: { label: '· Small', color: 'var(--text3)' },
  };
  const c = config[magnitude] || config.SMALL;
  return (
    <span style={{ fontSize: 10, color: c.color, fontWeight: 600, fontFamily: 'var(--font-display)' }}>
      {c.label}
    </span>
  );
}

function DirectionBadge({ direction, pctChange }) {
  const isSteam = direction === 'STEAM';
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      background: isSteam ? 'rgba(18,201,142,0.08)' : 'rgba(240,71,71,0.08)',
      border: `1px solid ${isSteam ? 'rgba(18,201,142,0.2)' : 'rgba(240,71,71,0.2)'}`,
      borderRadius: 'var(--radius-sm)', padding: '6px 10px', minWidth: 70, textAlign: 'center',
    }}>
      <span style={{ fontSize: 16 }}>{isSteam ? '📉' : '📈'}</span>
      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 12, color: isSteam ? 'var(--success)' : 'var(--danger)' }}>
        {direction}
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, color: isSteam ? 'var(--success)' : 'var(--danger)' }}>
        {pctChange > 0 ? '+' : ''}{pctChange?.toFixed(1)}%
      </span>
    </div>
  );
}

export default function SharpMoney() {
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [direction, setDirection] = useState('');
  const [magnitude, setMagnitude] = useState('');
  const [market, setMarket] = useState('');
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (direction) params.set('direction', direction);
      if (magnitude) params.set('magnitude', magnitude);
      if (market) params.set('market', market);

      const [data, sum] = await Promise.all([
        apiFetch(`/sharp-money?${params}`),
        apiFetch('/sharp-money/summary'),
      ]);
      setMovements(data.movements || []);
      setSummary(sum);
      setLastRefresh(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [direction, magnitude, market]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
  const t = setInterval(() => {
    if (!document.hidden) fetchData();
  }, 120000);
  
  const handleVisibility = () => {
    if (!document.hidden) fetchData();
  };
  document.addEventListener('visibilitychange', handleVisibility);
  
  return () => {
    clearInterval(t);
    document.removeEventListener('visibilitychange', handleVisibility);
  };
}, [fetchData]);

  return (
    <div className="no-select" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Watermark />

      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
        <div className="stat-card" style={{ '--accent-color': 'var(--accent)' }}>
          <div className="stat-label">Total Signals</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 18 : 24 }}>{loading ? '—' : summary?.total ?? 0}</div>
          <div className="stat-change neutral" style={{ fontSize: 10 }}>Last 24 hours</div>
        </div>
        <div className="stat-card" style={{ '--accent-color': 'var(--success)' }}>
          <div className="stat-label">Steam Moves</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 18 : 24 }}>{loading ? '—' : summary?.steam ?? 0}</div>
          <div className="stat-change up" style={{ fontSize: 10 }}>Money coming in</div>
        </div>
        <div className="stat-card" style={{ '--accent-color': 'var(--danger)' }}>
          <div className="stat-label">Large Moves</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 18 : 24 }}>{loading ? '—' : summary?.large ?? 0}</div>
          <div className="stat-change neutral" style={{ fontSize: 10 }}>20%+ movement</div>
        </div>
      </div>

      {/* Explainer */}
      <div style={{
        background: 'rgba(61,142,248,0.06)', border: '1px solid rgba(61,142,248,0.15)',
        borderRadius: 'var(--radius)', padding: '14px 16px',
        display: 'flex', gap: 12, alignItems: 'flex-start',
      }}>
        <div style={{ fontSize: 20, flexShrink: 0 }}>🔍</div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>What is sharp money?</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--success)' }}>STEAM</strong> — odds shortening fast means smart money is backing this outcome. Bookmakers cut prices when they take large bets from winning customers. &nbsp;
            <strong style={{ color: 'var(--danger)' }}>DRIFT</strong> — odds lengthening means money is going elsewhere, or bookmakers are adjusting their position.
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Direction</label>
            <select value={direction} onChange={e => setDirection(e.target.value)} style={{ fontSize: 13 }}>
              <option value="">All</option>
              <option value="STEAM">Steam only</option>
              <option value="DRIFT">Drift only</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Magnitude</label>
            <select value={magnitude} onChange={e => setMagnitude(e.target.value)} style={{ fontSize: 13 }}>
              <option value="">All</option>
              <option value="LARGE">Large (20%+)</option>
              <option value="MEDIUM">Medium (10-20%)</option>
              <option value="SMALL">Small (5-10%)</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Market</label>
            <select value={market} onChange={e => setMarket(e.target.value)} style={{ fontSize: 13 }}>
              <option value="">All markets</option>
              <option value="1x2">1X2</option>
              <option value="totals">Over/Under</option>
              <option value="btts">BTTS</option>
            </select>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            {lastRefresh && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
            <button className="btn btn-outline" style={{ fontSize: 11, padding: '5px 12px' }} onClick={fetchData} disabled={loading}>
              {loading ? '...' : '↺ Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Movements list */}
      <div className="card">
        {loading ? (
          <div style={{ padding: 20 }}>
            {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80, marginBottom: 10 }} />)}
          </div>
        ) : movements.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <p>No sharp money signals detected.</p>
            <p style={{ marginTop: 6, fontSize: 12 }}>Signals appear when odds move 5%+ on upcoming matches.</p>
          </div>
        ) : movements.map((m, i) => (
          <div key={i} style={{
            padding: isMobile ? '12px 14px' : '16px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex', gap: 12, alignItems: 'flex-start',
          }}>
            <DirectionBadge direction={m.direction} pctChange={m.pctChange} />

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                <div style={{ fontWeight: 600, fontSize: isMobile ? 13 : 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ⚽ {m.homeTeam} vs {m.awayTeam}
                </div>
                <MagnitudePill magnitude={m.magnitude} />
              </div>

              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.league} · {formatKickoff(m.commenceTime)}
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="pill pill-teal" style={{ fontSize: 10 }}>
                  {m.outcomeName} · {BOOKMAKER_LABELS[m.bookmaker] || m.bookmaker}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>
                  {m.firstPrice} → <strong style={{ color: 'var(--text)' }}>{m.lastPrice}</strong>
                </span>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                  {m.dataPoints} data points · {timeAgo(m.lastSeen)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}